import { Honeybadger } from '@honeybadger-io/react';
import { useContext } from 'react';
import { GroupsApi } from '../api/GroupApi';
import { UserApi, UserPermission } from '../api/UserApi';
import { AppContext } from '../contexts/AppContext';
import { Entity } from '../data/models/Entity';
import { Group } from '../data/models/Group';
import { User } from '../data/models/User';
import { EntitiesStore } from '../data/stores/EntitiesStore';
import { GroupStore } from '../data/stores/GroupStore';
import { TokenStore } from '../data/stores/TokenStore';
import { UserStore } from '../data/stores/UserStore';
import { ApportionmentsStore } from '../data/stores/ApportionmentsStore';
import { LoanAccountsStore } from '../data/stores/LoanAccountsStore';

export class GroupsController {
  ApportionmentsStore: ApportionmentsStore = null;
  UserStore: UserStore = null;
  GroupStore: GroupStore = null;
  EntitiesStore: EntitiesStore = null;
  LoanAccountsStore: LoanAccountsStore = null;

  constructor() {
    const {
      UserStore: userStore,
      GroupStore: groupStore,
      EntitiesStore: entitiesStore,
      ApportionmentsStore: apportionmentsStore,
      LoanAccountsStore: loanAccountsStore,
    } = useContext(AppContext);

    this.UserStore = userStore;
    this.GroupStore = groupStore;
    this.EntitiesStore = entitiesStore;
    this.ApportionmentsStore = apportionmentsStore;
    this.LoanAccountsStore = loanAccountsStore;
  }

  init = async () => {
    if (!TokenStore.instance().hasToken) {
      return;
    }

    const userApiResponse = await UserApi.getUser(this.UserStore.userId).catch(() => {
      // This can happen during local development when switching environments
      TokenStore.instance().reset();
      return null;
    });
    if (userApiResponse == null) {
      return;
    }
    const user = User.fromApi(userApiResponse);
    const groups = await UserApi.getGroups(user.id);
    this.UserStore.user = user;

    const groupPromises = groups.map(group => {
      user.addGroup(group.group, group.permissions as UserPermission);
      return GroupsApi.getGroup(group.group)
        .then(groupApiResponse => {
          this.GroupStore.setGroup(Group.fromApi(groupApiResponse));
        });
    });

    return Promise.all(groupPromises)
      .finally(() => {
        this.GroupStore.initialLoadCompleted = true;
      });
  };

  loadGroup = async (id: number) => {
    const group = this.GroupStore.getGroup(id);

    if (!group || group.loaded) return;

    this.ApportionmentsStore.loadGroupApportionments(id);

    if (!group.apiOrganisations) return;

    try {
      group.entities = [];
      const rulePromises = group.apiOrganisations.map(entityApiResponse => {
        const entity = Entity.fromApi(entityApiResponse);
        entity.groupId = group.id;
        group.entities.push(entity);
        this.EntitiesStore.addEntity(entity);

        entity.loadValues();
        return entity.loadRules();
      });

      group.archivedApiOrganisations.map(entityApiResponse => {
        const entity = Entity.fromApi(entityApiResponse);
        entity.groupId = group.id;
        group.archivedEntities.push(entity);
        this.EntitiesStore.addEntity(entity);
      });
      group.loadSources();
      this.LoanAccountsStore.loadLoanAccounts(group);

      await Promise.all(rulePromises);
    } catch (e) {
      console.error(e);
      Honeybadger.notify(e);
    } finally {
      group.loaded = true;
    }
  };

  reloadGroup = (groupId: number) => {
    this.GroupStore.loadGroup(groupId)
      .then(() => this.loadGroup(groupId));
  };
}
